import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { Pencil, Undo2, Archive as ArchiveIcon, ShieldAlert, Trash2 } from "lucide-react";

/**
 * Admin post-approval action bar for a single payment row.
 *   Correct → POST /payments/{id}/correct   (password + reason + new amounts)
 *   Reverse → POST /payments/{id}/reverse   (password + reason, soft-deletes)
 *   Archive → POST /payments/{id}/archive   (hide only, no password)
 *
 * Callers pass `payment` and `kind` ("loan" | "pigmy" | "investment").
 * `onDone` is called after any successful action so the parent can refetch.
 */
export function PaymentActions({ payment, kind, onDone }) {
    const [open, setOpen] = useState(null); // "correct" | "reverse" | "archive" | null
    const [busy, setBusy] = useState(false);
    const [pwd, setPwd] = useState("");
    const [reason, setReason] = useState("");
    const [interest, setInterest] = useState(Number(payment.interest_paid || 0));
    const [principal, setPrincipal] = useState(Number(payment.principal_paid || 0));
    const [total, setTotal] = useState(Number(payment.total || 0));
    const [payoutAmount, setPayoutAmount] = useState(Number(payment.amount || 0));
    const [payoutType, setPayoutType] = useState(String(payment.payout_type || "interest"));
    const [notes, setNotes] = useState(String(payment.notes || ""));

    const isInvestor = kind === "investor_payout";
    const basePath = isInvestor ? "/investor-payouts" : "/payments";

    function reset() {
        setPwd("");
        setReason("");
        setInterest(Number(payment.interest_paid || 0));
        setPrincipal(Number(payment.principal_paid || 0));
        setTotal(Number(payment.total || 0));
        setPayoutAmount(Number(payment.amount || 0));
        setPayoutType(String(payment.payout_type || "interest"));
        setNotes(String(payment.notes || ""));
    }
    function close() { setOpen(null); reset(); }

    async function doCorrect() {
        if (!pwd || !reason.trim()) return toast.error("Password and reason are required");
        setBusy(true);
        try {
            const body = { password: pwd, reason: reason.trim() };
            if (kind === "loan") {
                body.interest_paid = Number(interest) || 0;
                body.principal_paid = Number(principal) || 0;
            } else if (isInvestor) {
                body.amount = Number(payoutAmount) || 0;
                body.payout_type = payoutType;
                body.notes = notes;
            } else {
                body.total = Number(total) || 0;
            }
            await api.post(`${basePath}/${payment.id}/correct`, body);
            toast.success("Correction posted");
            close();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    }

    async function doReverse() {
        if (!pwd || !reason.trim()) return toast.error("Password and reason are required");
        setBusy(true);
        try {
            await api.post(`${basePath}/${payment.id}/reverse`, {
                password: pwd, reason: reason.trim(),
            });
            toast.success(isInvestor ? "Payout reversed" : "Payment reversed");
            close();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    }

    async function doArchive() {
        setBusy(true);
        try {
            await api.post(`${basePath}/${payment.id}/archive`, { reason: reason.trim() });
            toast.success("Row archived");
            close();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    }

    async function doDelete() {
        if (!pwd || !reason.trim()) return toast.error("Password and reason are required");
        setBusy(true);
        try {
            await api.delete(`${basePath}/${payment.id}/permanent`, {
                data: { password: pwd, reason: reason.trim() },
            });
            toast.success("Entry permanently deleted");
            close();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    }

    const deleteButton = (
        <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs rounded-md text-red-800 border-red-300 hover:bg-red-50"
            data-testid={`delete-payment-${payment.id}`}
            onClick={() => setOpen("delete")}
        >
            <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
    );

    const dialog = (
        <Dialog open={!!open} onOpenChange={(o) => { if (!o) close(); }}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="payment-actions-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-blue-900" />
                            {open === "correct" && "Correct Payment"}
                            {open === "reverse" && "Reverse / Delete Payment"}
                            {open === "archive" && "Archive Payment"}
                            {open === "delete" && "Delete Entry Permanently"}
                        </DialogTitle>
                    </DialogHeader>

                    {open === "delete" && (
                        <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md p-2" data-testid="delete-warning">
                            This removes the entry, its ledger lines and its effect on all balances,
                            statements and reports — exactly as if it was never recorded. This cannot be undone.
                        </p>
                    )}

                    {open === "correct" && (
                        <div className="space-y-3">
                            {kind === "loan" ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Interest ₹</Label>
                                        <Input type="number" value={interest}
                                               onChange={(e) => setInterest(e.target.value)}
                                               data-testid="correct-interest" />
                                    </div>
                                    <div>
                                        <Label>Principal ₹</Label>
                                        <Input type="number" value={principal}
                                               onChange={(e) => setPrincipal(e.target.value)}
                                               data-testid="correct-principal" />
                                    </div>
                                </div>
                            ) : isInvestor ? (
                                <div className="space-y-3">
                                    <div>
                                        <Label>Amount ₹</Label>
                                        <Input type="number" value={payoutAmount}
                                               onChange={(e) => setPayoutAmount(e.target.value)}
                                               data-testid="correct-payout-amount" />
                                    </div>
                                    <div>
                                        <Label>Payout Type</Label>
                                        <select
                                            value={payoutType}
                                            onChange={(e) => setPayoutType(e.target.value)}
                                            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                                            data-testid="correct-payout-type"
                                        >
                                            <option value="interest">Interest</option>
                                            <option value="principal">Principal</option>
                                            <option value="maturity">Maturity</option>
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Notes</Label>
                                        <Input value={notes}
                                               onChange={(e) => setNotes(e.target.value)}
                                               data-testid="correct-payout-notes" />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Label>Amount ₹</Label>
                                    <Input type="number" value={total}
                                           onChange={(e) => setTotal(e.target.value)}
                                           data-testid="correct-total" />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="space-y-3 mt-2">
                        {open !== "archive" && (
                            <div>
                                <Label>Admin Password</Label>
                                <Input
                                    type="password"
                                    autoComplete="current-password"
                                    value={pwd}
                                    onChange={(e) => setPwd(e.target.value)}
                                    data-testid="action-password"
                                />
                            </div>
                        )}
                        <div>
                            <Label>Reason {open !== "archive" && (<span className="text-rose-700">*</span>)}</Label>
                            <Textarea
                                rows={2}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder={open === "correct" ? "Wrong amount entered…"
                                    : open === "reverse" ? "Duplicate / entered on wrong loan…"
                                    : open === "delete" ? "Test entry / recorded by mistake…"
                                    : "Hidden from customer view…"}
                                data-testid="action-reason"
                            />
                        </div>
                        {(open === "correct" || open === "reverse") && (
                            <p className="text-[11px] text-slate-500">
                                A matching reversal voucher is posted on the ledger. This cannot be undone.
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
                        {open === "correct" && (
                            <Button onClick={doCorrect} disabled={busy}
                                    className="bg-blue-900 hover:bg-blue-800"
                                    data-testid="confirm-correct">
                                {busy ? "Saving…" : "Post correction"}
                            </Button>
                        )}
                        {open === "reverse" && (
                            <Button onClick={doReverse} disabled={busy}
                                    className="bg-rose-700 hover:bg-rose-800"
                                    data-testid="confirm-reverse">
                                {busy ? "Saving…" : "Reverse & delete"}
                            </Button>
                        )}
                        {open === "archive" && (
                            <Button onClick={doArchive} disabled={busy}
                                    className="bg-slate-800 hover:bg-slate-900"
                                    data-testid="confirm-archive">
                                {busy ? "Saving…" : "Archive"}
                            </Button>
                        )}
                        {open === "delete" && (
                            <Button onClick={doDelete} disabled={busy}
                                    className="bg-red-800 hover:bg-red-900"
                                    data-testid="confirm-delete">
                                {busy ? "Deleting…" : "Delete permanently"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
    );

    if (payment.reversed || payment.archived) {
        return (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span
                    className="text-[10px] uppercase tracking-wide font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-1.5 py-0.5"
                    data-testid={`payment-status-${payment.id}`}
                >
                    {payment.reversed ? "Reversed" : "Archived"}
                </span>
                {deleteButton}
                {dialog}
            </div>
        );
    }

    return (
        <>
            <div className="mt-2 flex flex-wrap gap-1.5">
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs rounded-md"
                    data-testid={`correct-payment-${payment.id}`}
                    onClick={() => setOpen("correct")}
                >
                    <Pencil className="h-3 w-3 mr-1" /> Correct
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs rounded-md text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid={`reverse-payment-${payment.id}`}
                    onClick={() => setOpen("reverse")}
                >
                    <Undo2 className="h-3 w-3 mr-1" /> Reverse
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs rounded-md text-slate-600"
                    data-testid={`archive-payment-${payment.id}`}
                    onClick={() => setOpen("archive")}
                >
                    <ArchiveIcon className="h-3 w-3 mr-1" /> Archive
                </Button>
                {deleteButton}
            </div>
            {dialog}
        </>
    );
}
